/**
 * Programmes — the shell for the programme library and the new-programme builder.
 *
 * Mirrors the SkillDrillBuilder + MicroClassBuilder pattern: tabs are
 * URL-driven so the sidebar deep-links to each one.
 *
 *   /scenario-builder           → Create Scenario Programme  (default)
 *   /scenario-builder/library   → Programmes (the library)
 *
 * Spec: Documentation/specs/scenario-builder-v1.md. Backend:
 * `/api/cc/scenario-programmes/*` (9 endpoints, all wired).
 */
import { lazy, Suspense, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Lazy-load each tab to keep the initial chunk small.
const ScenarioCreator = lazy(() => import('./tabs/ScenarioCreator'));
const ScenarioLibrary = lazy(() => import('./tabs/ScenarioLibrary'));

type SBTab = 'create' | 'library';

const tabPaths: Record<string, SBTab> = {
  '': 'create',
  library: 'library',
};

const BASE = '/scenario-builder';

function TabLoader() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="flex flex-col items-center gap-2">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  );
}

export default function ScenarioBuilder() {
  const { pathname } = useLocation();

  const activeTab: SBTab = useMemo(() => {
    const segment = pathname.replace(BASE, '').replace(/^\//, '').split('/')[0];
    return tabPaths[segment] ?? 'create';
  }, [pathname]);


  return (
    <div className="space-y-6">
      <Suspense fallback={<TabLoader />}>
        {activeTab === 'create' && <ScenarioCreator />}
        {activeTab === 'library' && <ScenarioLibrary />}
      </Suspense>
    </div>
  );
}
